import { cva } from "cva";
import { format } from "date-fns";
import { BuildHtml } from "~/components/BuildHtml";
import { ErrorBoundary } from "~/components/ErrorBoundary";
import { PublicationFrequency } from "~/components/PublicationFrequency";
import { PublicationFrequencyType } from "~/types/asgard/Tracker";
import { Body, Headline, Icon } from "~/ui-library";
import { ClampedText } from "~/ui-library/components/ClampedText";
import { RecentIcon } from "~/ui-library/icons";

const iconStyles = cva("fill-transparent stroke-2 ", {
  variants: {
    publicationFrequencyValue: {
      updates_monthly: "stroke-brand-green-800",
      updates_quarterly: "stroke-brand-green-800",
      updates_weekly: "stroke-brand-green-800",
      this_tracker_is_no_longer_being_updated: "stroke-danger-800",
      other: "stroke-brand-green-800",
    } as Record<PublicationFrequencyType, string>,
  },
});

type PublicationUpdatedProps = Pick<
  TrackerHeaderProps,
  "publicationFrequencyValue"
> & {
  date: string;
};

const isDateStringValid = (date: string | null): date is string => !!date;

const PublicationUpdated = ({
  date,
  publicationFrequencyValue,
}: PublicationUpdatedProps) => {
  switch (publicationFrequencyValue) {
    case "this_tracker_is_no_longer_being_updated":
      return (
        <div className="flex items-center gap-xxs">
          <Icon
            className={iconStyles({ publicationFrequencyValue })}
            SvgIcon={RecentIcon}
            size="small"
          />
          <Body size="small">
            Last updated on {format(new Date(date), "MMM d, yyyy")}. This
            tracker is no longer being updated.
          </Body>
        </div>
      );
    default:
      return (
        <div className="flex items-center gap-xxs">
          <Icon
            className={iconStyles({ publicationFrequencyValue })}
            SvgIcon={RecentIcon}
            size="small"
          />
          <Body size="small">
            Updated on {format(new Date(date), "MMM d, yyyy")}
          </Body>
        </div>
      );
  }
};

export type PublicationInfoProps = Pick<
  TrackerHeaderProps,
  "date" | "publicationFrequencyValue" | "publicationFrequencyLabel"
>;

const PublicationInfo = ({
  date,
  publicationFrequencyValue,
  publicationFrequencyLabel,
}: PublicationInfoProps) => (
  <div className="mb-s flex flex-wrap gap-s md:gap-m">
    <ErrorBoundary
      fallback={<></>}
      context={{
        component: "TrackerHeader > PublicationInfo",
        date,
        publicationFrequencyValue,
      }}
    >
      {isDateStringValid(date) && (
        <PublicationUpdated
          date={date}
          publicationFrequencyValue={publicationFrequencyValue}
        />
      )}
    </ErrorBoundary>
    {publicationFrequencyValue !== "this_tracker_is_no_longer_being_updated" ? (
      <PublicationFrequency
        publicationFrequency={publicationFrequencyValue}
        showIcon={publicationFrequencyValue !== "other"}
      >
        {publicationFrequencyLabel}
      </PublicationFrequency>
    ) : null}
  </div>
);

export type TrackerHeaderProps = {
  publicationFrequencyLabel: string | null;
  publicationFrequencyValue: PublicationFrequencyType;
  date: string | null;
  title: string;
  subtitle: string;
  chartDescription: string;
};

export const TrackerHeader: React.FC<TrackerHeaderProps> = ({
  publicationFrequencyValue: publicationFrequency,
  publicationFrequencyLabel,
  date,
  title,
  subtitle,
  chartDescription,
}) => (
  <div className="max-w-[800px] pt-l xl:pt-xl print:max-w-full">
    <div className="print:hidden">
      <PublicationInfo
        date={date}
        publicationFrequencyLabel={publicationFrequencyLabel}
        publicationFrequencyValue={publicationFrequency}
      />
    </div>
    <div>
      <Headline
        as="h1"
        level={2}
        mdLevel={0}
        className="mb-s print:text-headline0 print:font-bold"
      >
        {title}
      </Headline>

      <Body size="large">{subtitle}</Body>
      {chartDescription?.length ? (
        <ClampedText className="mt-s" lineCount={2}>
          <BuildHtml allowStyles html={chartDescription} />
        </ClampedText>
      ) : null}
    </div>
  </div>
);
